import React from 'react';
import { CalendarClock, Workflow, CheckCircle2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DEPARTMENTS } from '../../data/mockData';
import { useHGS } from '../../context/HGSContext';

const WORKFLOW_STEPS = [
  'Admin uploads & publishes masters',
  'Operating Head fills + publishes Risk Universe',
  'System dispatches dept-scoped PCC / Fraud / DLC tasks',
  'Department Heads submit responses',
  'Division Head reviews & approves',
  'Operating Head grants final approval',
  'Legal / GRC views consolidated dashboard',
];

export default function GovernanceCycleTab() {
  const { riskPublished, publishRiskAssessment } = useHGS();
  const isLive = !!riskPublished?.published;

  return (
    <div className="space-y-5" data-testid="governance-cycle">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">Governance Cycle Setup</h3>
          <p className="text-xs text-slate-500">
            Define the yearly cycle window, then publish the Risk Universe to dispatch tasks across departments.
          </p>
        </div>
        <span className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border',
          isLive ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                 : 'bg-amber-50 text-amber-700 border-amber-200'
        )}>
          <span className={cn('w-1.5 h-1.5 rounded-full', isLive ? 'bg-emerald-500' : 'bg-amber-500')} />
          {isLive ? `Live · ${riskPublished.version}` : 'Cycle Draft'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-4 h-4 text-violet-700" />
            <div className="text-sm font-bold text-slate-900">Cycle window</div>
          </div>
          <dl className="space-y-2 text-xs">
            <div className="flex justify-between"><dt className="text-slate-500">Cycle</dt><dd className="font-mono font-bold text-slate-900">FY26 · Q3</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Window opens</dt><dd className="font-mono text-slate-700">2026-01-01</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Window closes</dt><dd className="font-mono text-slate-700">2026-03-31</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Departments in scope</dt><dd className="font-bold text-slate-900">{DEPARTMENTS.length}</dd></div>
          </dl>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <Workflow className="w-4 h-4 text-emerald-700" />
            <div className="text-sm font-bold text-slate-900">Workflow steps</div>
          </div>
          <ol className="space-y-1.5 text-xs text-slate-700">
            {WORKFLOW_STEPS.map((s, i) => (
              <li key={s} className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-slate-100 text-slate-700 text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#CC0000] text-white flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-slate-900">Publish governance cycle</div>
            <p className="text-xs text-slate-600 mt-0.5">
              Triggers the Operating Head's Risk Universe publish — tasks are dispatched to all department dashboards.
              Re-publishing increments the cycle version.
            </p>
          </div>
          <button
            data-testid="publish-cycle-btn"
            onClick={publishRiskAssessment}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-bold rounded-lg shadow-sm whitespace-nowrap"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {isLive ? 'Re-publish cycle' : 'Publish cycle'}
          </button>
        </div>
      </div>
    </div>
  );
}
